import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { clearIndependentSession, hydrateIndependentSessionFromLegacy, readIndependentSession, readLegacySession, revokeIndependentSession, saveIndependentSession } from "@/features/auth/independent";
import { request, setSessionInvalidHandler } from "@/lib/apiClient";
import { setOperatingCountryCode } from "@/lib/money";
import { getWebPendingSyncQueueItems, processWebSyncQueue } from "@/lib/offline/webOfflineStore";

const AuthCtx = createContext(null);

function readActiveSessionState() {
  if (typeof window === "undefined") {
    return { session: null };
  }

  const storedSession = readIndependentSession();
  if (storedSession) {
    return { session: storedSession };
  }

  const legacySession = readLegacySession();
  if (legacySession && (!legacySession.authMode || legacySession.authMode === "independent")) {
    return { session: hydrateIndependentSessionFromLegacy(legacySession) };
  }

  return { session: null };
}

export function AuthProvider({ children }) {
  const initialAuthState = useMemo(() => readActiveSessionState(), []);
  const [session, setSession] = useState(initialAuthState.session);
  setOperatingCountryCode(session?.subscription?.billingCountryCode);
  const clearLocalSession = useCallback(() => {
    clearIndependentSession();
    setSession(null);
  }, []);

  const updateSession = useCallback((nextSession) => {
    if (!nextSession) {
      clearLocalSession();
      return null;
    }
    const normalized = saveIndependentSession(nextSession);
    setSession(normalized);
    return normalized;
  }, [clearLocalSession]);

  useEffect(() => {
    setSessionInvalidHandler((reason) => {
      if (reason?.code === "SESSION_REPLACED") {
        try {
          window.sessionStorage.setItem("shopbook.web.session-exit-reason", JSON.stringify(reason));
        } catch {
          // Session displacement is still enforced if browser storage is unavailable.
        }
      }
      clearLocalSession();
    });

    return () => {
      setSessionInvalidHandler(null);
    };
  }, [clearLocalSession]);

  useEffect(() => {
    if (!session?.sessionId) return undefined;

    let active = true;
    const verifySession = () => {
      if (!active || (typeof document !== "undefined" && document.visibilityState === "hidden")) return;
      const sessionId = session.sessionId;
      void request("/api/v1/me", { sessionId })
        .then((response) => {
          if (active && response?.session) {
            updateSession({
              ...response.session,
              authMode: "independent",
              sessionId,
            });
          }
        })
        .catch(() => {
          // Structured authentication failures are handled centrally.
        });
    };
    verifySession();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") verifySession();
    };
    const interval = window.setInterval(verifySession, 30_000);
    window.addEventListener("focus", verifySession);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      active = false;
      window.clearInterval(interval);
      window.removeEventListener("focus", verifySession);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [session?.sessionId, updateSession]);

  const signInIndependent = useCallback(
    (nextSession) => {
      const normalized = saveIndependentSession({
        ...nextSession,
        authMode: "independent",
        sessionSource: "web_direct",
      });
      setSession(normalized);
      return normalized;
    },
    []
  );

  const logout = useCallback(async ({ force = false } = {}) => {
    if (session?.sessionId) {
      let pending = await getWebPendingSyncQueueItems(session).catch(() => []);
      if (!force && pending.length && (typeof navigator === "undefined" || navigator.onLine)) {
        await processWebSyncQueue(session).catch(() => null);
        pending = await getWebPendingSyncQueueItems(session).catch(() => pending);
      }
      if (
		!force &&
        pending.length &&
        typeof window !== "undefined" &&
        !window.confirm(
          `${pending.length} local change${pending.length === 1 ? "" : "s"} still need to sync. Log out and keep them safely on this browser for recovery after you sign in again?`,
        )
      ) {
        return false;
      }
      try {
        await revokeIndependentSession(session.sessionId);
      } catch (error) {
        if (error?.status !== 401) {
          throw error;
        }
        // An already-invalid server session is equivalent to being signed out.
      }
    }

    clearLocalSession();
    return true;
  }, [clearLocalSession, session]);

  const value = useMemo(
    () => ({
      logout,
      session,
      setSession: updateSession,
      signInIndependent,
      updateSession,
    }),
    [logout, session, signInIndependent, updateSession]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
